import { type ReactNode, useState } from "react";
import {
  ChevronLeftIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { ThreadStatusPills } from "@/app/Threads/Components/ThreadStatusPills";
import { ThreadSortMenu } from "@/app/Threads/Components/ThreadSortMenu";
import { ThreadList } from "@/app/Threads/Components/ThreadList";
import { ThreadsQueryThread } from "@/app/Threads/GraphQL/threadQueries";
import { HeldThread, useHeldThread } from "@/app/Threads/hooks/useHeldThread";
import { nextThreadAfter } from "@/app/Threads/utils/nextThreadAfter";
import { ThreadSelectionContext } from "@/app/Threads/hooks/useThreadSelection";
import { Input, InputGroup } from "@/components/catalyst/input";
import { ThreadSort, ThreadStatusFilter } from "@/gql/graphql";
import { Outlet } from "@tanstack/react-router";
import { cn } from "@/lib/utils";

type ThreadListPanelProps = {
  threads: ThreadsQueryThread[];
  isLoading: boolean;
  headerAction?: ReactNode;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  selectedId: string | null;
  onSelect: (thread: ThreadsQueryThread) => void;
  /** Back to the bare list: the way out of a thread on small screens, and where a status-filtered list goes when its last thread leaves it. */
  onClearSelection: () => void;
  search: string;
  onSearchChange: (value: string) => void;
  status?: ThreadStatusFilter;
  onStatusChange?: (status: ThreadStatusFilter) => void;
  unreadCounts?: Partial<Record<ThreadStatusFilter, number>>;
  sort: ThreadSort;
  onSortChange: (sort: ThreadSort) => void;
};

export function ThreadListPanel({
  threads,
  isLoading,
  headerAction,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  selectedId,
  onSelect,
  onClearSelection,
  search,
  onSearchChange,
  status,
  onStatusChange,
  unreadCounts,
  sort,
  onSortChange,
}: ThreadListPanelProps) {
  const [held, setHeld] = useState<HeldThread | null>(null);

  const listKey = `${status ?? ""}::${sort}::${search}`;
  const listedThreads = useHeldThread(threads, selectedId, held, listKey);

  const selection = {
    selectNextAfter: (threadId: string) => {
      // Without a status filter nothing leaves this list when it closes, so
      // there is nothing to move off.
      if (status === undefined || threadId !== selectedId) {
        return;
      }

      setHeld(null);

      // The rendered list, so a held row counts as the position to move from.
      const next = nextThreadAfter(listedThreads, threadId);

      if (next) {
        onSelect(next);
        return;
      }

      if (next === null) {
        onClearSelection();
      }
    },
    holdInList: (threadId: string) => {
      const index = listedThreads.findIndex((thread) => thread.id === threadId);

      if (index !== -1) {
        setHeld({ thread: listedThreads[index], index, listKey });
      }
    },
  };

  return (
    <ThreadSelectionContext.Provider value={selection}>
      <div
        className={cn(
          "flex w-full shrink-0 flex-col overflow-hidden lg:w-96 lg:border-r lg:border-gray-200",
          selectedId ? "max-lg:hidden" : null,
        )}
      >
        <div className="flex flex-col gap-2 border-b border-gray-200 px-4 py-2">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <InputGroup>
                <MagnifyingGlassIcon data-slot="icon" />
                <Input
                  name="thread-search"
                  type="text"
                  autoComplete="off"
                  placeholder="Search threads"
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                />
              </InputGroup>
            </div>
            <ThreadSortMenu sort={sort} onSortChange={onSortChange} />
            {headerAction}
          </div>
          {status && onStatusChange ? (
            <ThreadStatusPills
              status={status}
              onStatusChange={onStatusChange}
              unreadCounts={unreadCounts}
            />
          ) : null}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center p-4 text-sm text-gray-500">
            Loading...
          </div>
        ) : (
          <ThreadList
            threads={listedThreads}
            selectedId={selectedId}
            onSelect={onSelect}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            search={search}
          />
        )}
      </div>
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          selectedId ? null : "max-lg:hidden",
        )}
      >
        <button
          type="button"
          onClick={onClearSelection}
          className="flex items-center gap-1 border-b border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 lg:hidden"
        >
          <ChevronLeftIcon className="size-4" />
          All threads
        </button>
        <Outlet />
      </div>
    </ThreadSelectionContext.Provider>
  );
}
